// ./src/components/features/EventMap/EventMapBoard/EventMapBoard.tsx
//
// Interactive Google Map that renders an event's on-site map elements as
// category-colored pins. Two modes via `editable`:
//   • editable (management page) — pins are draggable (→ onMoveElement) and a new
//     pin is dropped (→ onPlace) either when `placing` is on OR on a ⌘/Ctrl-click.
//     The selected pin is emphasized.
//   • read-only (public widget) — clicking a pin calls onSelectElement so the
//     caller can show a popup with the element's info + schedule.
//
// Exposes an imperative `pointToLatLng(clientX, clientY)` (via ref) so a caller can
// translate a drag-and-drop drop point on the map into coordinates.
//
// Uses the shared GoogleMapsProvider (maps + marker libraries → AdvancedMarker).
// Elements without coordinates are simply not drawn (the caller lists them
// separately as "not placed yet").

import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { Box, Center, Loader, Text, Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { EventMapElement, MapPoint } from "@/utils/api";
import { useGoogleMapsDisplay } from "@/contexts/GoogleMapsContext";
import { AppIcon } from "@/components/generic";

import { MapCategoryMeta, markerColorOf, markerGlyph, hasCoords, DEFAULT_MARKER_COLOR } from "../mapHelpers/mapHelpers";

/** A read-only zone polygon to draw under the element pins. */
export interface ZoneShape {
  id: string;
  color: string | null;
  points: MapPoint[];
}

// An arbitrary non-empty mapId enables AdvancedMarkerElement (default styling).
const MAP_ID = "event-map-board";
const DEFAULT_CENTER = { lat: 40, lng: -100 };
const DEFAULT_ZOOM = 15;

// The pin's glyph: the category icon (rendered white SVG) when there is one, else
// the first letter of the name. Google's PinElement accepts an Element or a string.
const buildGlyph = (iconName: string | null, name: string | null | undefined): Element | string => {
  if (iconName) {
    try {
      const markup = renderToStaticMarkup(<AppIcon iconName={iconName} size={16} color="#ffffff" />);
      if (markup) {
        const span = document.createElement("span");
        span.style.display = "flex";
        span.style.alignItems = "center";
        span.style.justifyContent = "center";
        span.style.color = "#ffffff";
        span.innerHTML = markup;
        return span;
      }
    } catch {
      // fall through to the letter glyph
    }
  }
  return markerGlyph(name);
};

export interface EventMapBoardProps {
  elements: EventMapElement[];
  /** Category meta (color/icon/name) for a given categoryId. */
  metaOf: (categoryId: string | null | undefined) => MapCategoryMeta | null;
  /** Initial center (e.g. the event venue's coordinates). */
  center?: { lat: number; lng: number } | null;
  /** The event venue — drawn as a distinct, non-interactive "home" pin. */
  venuePoint?: { lat: number; lng: number; label?: string | null } | null;
  /** Imperative pan target — when this changes, the map recenters + zooms to it. */
  focusPoint?: { lat: number; lng: number } | null;
  height?: number | string;
  editable?: boolean;
  /** When true (+ editable), a map click drops a new pin. */
  placing?: boolean;
  selectedId?: string | null;
  onPlace?: (lat: number, lng: number) => void;
  onSelectElement?: (element: EventMapElement) => void;
  onMoveElement?: (id: string, lat: number, lng: number) => void;

  // ── Zones (area polygons drawn under the pins) ──
  /** Read-only zone polygons. */
  zones?: ZoneShape[];
  selectedZoneId?: string | null;
  onSelectZone?: (id: string) => void;
  /** The polygon currently being drawn/edited (editable vertices). */
  editingZone?: { points: MapPoint[]; color?: string | null } | null;
  /** When true, a map click appends a vertex to the editing zone. */
  zoneDrawMode?: boolean;
  onEditingZoneChange?: (points: MapPoint[]) => void;
}

/** Imperative handle: translate a screen point (e.g. a drop) into map coordinates. */
export interface EventMapBoardHandle {
  pointToLatLng: (clientX: number, clientY: number) => { lat: number; lng: number } | null;
  /** Remove the last-added vertex of the zone being drawn. */
  removeLastZonePoint: () => void;
  /** Clear every vertex of the zone being drawn. */
  clearZonePoints: () => void;
}

export const EventMapBoard = forwardRef<EventMapBoardHandle, EventMapBoardProps>(({
  elements,
  metaOf,
  center,
  venuePoint = null,
  focusPoint = null,
  height = 460,
  editable = false,
  placing = false,
  selectedId = null,
  onPlace,
  onSelectElement,
  onMoveElement,
  zones = [],
  selectedZoneId = null,
  onSelectZone,
  editingZone = null,
  zoneDrawMode = false,
  onEditingZoneChange,
}, ref) => {
  const { t } = useTranslation(["events", "common"]);
  const { libraries, isLoaded, isLoading, error } = useGoogleMapsDisplay();

  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<Map<string, google.maps.marker.AdvancedMarkerElement>>(new Map());
  const clickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const overlayRef = useRef<google.maps.OverlayView | null>(null);
  const venueMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const zonePolysRef = useRef<Map<string, google.maps.Polygon>>(new Map());
  const editZonePolyRef = useRef<google.maps.Polygon | null>(null);
  const editZoneListenersRef = useRef<google.maps.MapsEventListener[]>([]);
  const editZoneEdgeLabelsRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const didFitRef = useRef(false);

  // Keep the latest callbacks/flags in refs so the marker listeners (bound once)
  // always see current values without re-binding.
  const placingRef = useRef(placing);
  const onPlaceRef = useRef(onPlace);
  const onSelectRef = useRef(onSelectElement);
  const onMoveRef = useRef(onMoveElement);
  const editableRef = useRef(editable);
  const onSelectZoneRef = useRef(onSelectZone);
  const onEditingZoneChangeRef = useRef(onEditingZoneChange);
  const zoneDrawModeRef = useRef(zoneDrawMode);
  useEffect(() => {
    placingRef.current = placing;
    onPlaceRef.current = onPlace;
    onSelectRef.current = onSelectElement;
    onMoveRef.current = onMoveElement;
    editableRef.current = editable;
    onSelectZoneRef.current = onSelectZone;
    onEditingZoneChangeRef.current = onEditingZoneChange;
    zoneDrawModeRef.current = zoneDrawMode;
  }, [placing, onPlace, onSelectElement, onMoveElement, editable, onSelectZone, onEditingZoneChange, zoneDrawMode]);

  // Read a Google polygon path back into plain {lat,lng} points.
  const pathToPoints = (poly: google.maps.Polygon): MapPoint[] =>
    poly
      .getPath()
      .getArray()
      .map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));

  const placedElements = useMemo(() => elements.filter(hasCoords), [elements]);

  const initialCenter = useMemo(() => {
    if (center && typeof center.lat === "number" && typeof center.lng === "number") {
      return center;
    }

    const first = placedElements[0];
    if (first) {
      return { lat: first.latitude as number, lng: first.longitude as number };
    }

    return DEFAULT_CENTER;
  }, [center, placedElements]);

  // ── Create the map once the libraries are ready ──
  useEffect(() => {
    if (!isLoaded || !libraries || !containerRef.current || mapRef.current) {
      return;
    }

    const map = new libraries.maps.Map(containerRef.current, {
      center: initialCenter,
      zoom: DEFAULT_ZOOM,
      mapId: MAP_ID,
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
      clickableIcons: false,
    });
    mapRef.current = map;

    // A no-op overlay just to borrow its projection (screen pixel ↔ lat/lng) for
    // drag-and-drop drops from the list. LatLngBounds / OverlayView / Point live on
    // the core `google.maps` namespace, not on the loaded `maps` library object.
    const overlay = new google.maps.OverlayView();
    overlay.onAdd = () => undefined;
    overlay.draw = () => undefined;
    overlay.onRemove = () => undefined;
    overlay.setMap(map);
    overlayRef.current = overlay;

    clickListenerRef.current = map.addListener("click", (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) {
        return;
      }
      // Drawing a zone: each click appends a vertex to the editable polygon.
      if (zoneDrawModeRef.current && editZonePolyRef.current) {
        editZonePolyRef.current.getPath().push(e.latLng);
        onEditingZoneChangeRef.current?.(pathToPoints(editZonePolyRef.current));
        return;
      }
      // Drop a new pin when placing mode is on OR on a ⌘/Ctrl-click.
      const domEvent = (e as unknown as { domEvent?: MouseEvent }).domEvent;
      const withModifier = !!(domEvent && (domEvent.metaKey || domEvent.ctrlKey));
      if (editableRef.current && (placingRef.current || withModifier) && onPlaceRef.current) {
        onPlaceRef.current(e.latLng.lat(), e.latLng.lng());
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, libraries]);

  // ── Imperative: translate a screen point (drag-drop) into map coordinates ──
  useImperativeHandle(
    ref,
    (): EventMapBoardHandle => ({
      pointToLatLng: (clientX, clientY) => {
        const overlay = overlayRef.current;
        const container = containerRef.current;
        const projection = overlay?.getProjection();
        if (!overlay || !container || !projection || typeof google === "undefined") {
          return null;
        }
        const rect = container.getBoundingClientRect();
        const point = new google.maps.Point(clientX - rect.left, clientY - rect.top);
        const latLng = projection.fromContainerPixelToLatLng(point);
        return latLng ? { lat: latLng.lat(), lng: latLng.lng() } : null;
      },
      removeLastZonePoint: () => {
        const poly = editZonePolyRef.current;
        if (!poly) {
          return;
        }
        const path = poly.getPath();
        if (path.getLength() > 0) {
          path.removeAt(path.getLength() - 1);
          onEditingZoneChangeRef.current?.(pathToPoints(poly));
        }
      },
      clearZonePoints: () => {
        const poly = editZonePolyRef.current;
        if (!poly) {
          return;
        }
        poly.getPath().clear();
        onEditingZoneChangeRef.current?.(pathToPoints(poly));
      },
    }),
    []
  );

  // ── Reflect the "placing" cursor on the map container ──
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.cursor = editable && placing ? "crosshair" : "";
    }
  }, [editable, placing]);

  // ── Reconcile markers whenever the elements / selection change ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded || !libraries) {
      return;
    }

    const markerLib = libraries.marker;
    const seen = new Set<string>();

    placedElements.forEach((el) => {
      const id = el.id as string;
      seen.add(id);
      const meta = metaOf(el.categoryId);
      const color = markerColorOf(meta);
      const isSelected = id === selectedId;

      const pin = new markerLib.PinElement({
        background: color,
        borderColor: isSelected ? "#1d1d1f" : color,
        glyphColor: "#ffffff",
        glyph: buildGlyph(meta?.icon ?? null, el.name),
        scale: isSelected ? 1.3 : 1,
      });

      let marker = markersRef.current.get(id);
      if (!marker) {
        marker = new markerLib.AdvancedMarkerElement({
          map,
          position: { lat: el.latitude as number, lng: el.longitude as number },
        });
        marker.addListener("click", () => {
          if (onSelectRef.current) {
            const current = placedElements.find((e) => e.id === id);
            if (current) {
              onSelectRef.current(current);
            }
          }
        });
        marker.addListener("dragend", () => {
          const pos = marker!.position;
          if (pos && onMoveRef.current) {
            const lat = typeof pos.lat === "function" ? (pos.lat as () => number)() : (pos.lat as number);
            const lng = typeof pos.lng === "function" ? (pos.lng as () => number)() : (pos.lng as number);
            onMoveRef.current(id, lat, lng);
          }
        });
        markersRef.current.set(id, marker);
      }

      marker.position = { lat: el.latitude as number, lng: el.longitude as number };
      marker.content = pin.element;
      marker.title = el.name || "";
      marker.gmpDraggable = editable;
      marker.zIndex = isSelected ? 1000 : undefined;
    });

    // Remove markers whose element is gone / lost its coords.
    markersRef.current.forEach((marker, id) => {
      if (!seen.has(id)) {
        marker.map = null;
        markersRef.current.delete(id);
      }
    });
  }, [placedElements, selectedId, editable, isLoaded, libraries, metaOf]);

  // ── The event venue: a distinct, non-interactive "home" pin ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded || !libraries) {
      return;
    }

    const markerLib = libraries.marker;
    const valid = venuePoint && typeof venuePoint.lat === "number" && typeof venuePoint.lng === "number";

    if (!valid) {
      if (venueMarkerRef.current) {
        venueMarkerRef.current.map = null;
        venueMarkerRef.current = null;
      }
      return;
    }

    const pin = new markerLib.PinElement({
      background: "#1d1d1f",
      borderColor: "#1d1d1f",
      glyphColor: "#ffffff",
      glyph: buildGlyph("Home", venuePoint!.label || null),
      scale: 1.2,
    });

    if (!venueMarkerRef.current) {
      venueMarkerRef.current = new markerLib.AdvancedMarkerElement({ map });
    }
    venueMarkerRef.current.position = { lat: venuePoint!.lat, lng: venuePoint!.lng };
    venueMarkerRef.current.content = pin.element;
    venueMarkerRef.current.title = venuePoint!.label || "Venue";
    venueMarkerRef.current.zIndex = 500;
  }, [venuePoint, isLoaded, libraries]);

  // ── Read-only zone polygons (drawn under the pins) ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded || !libraries) {
      return;
    }
    const seen = new Set<string>();
    zones.forEach((z) => {
      if (!z.points || z.points.length < 3) {
        return;
      }
      seen.add(z.id);
      const color = z.color || DEFAULT_MARKER_COLOR;
      const isSel = z.id === selectedZoneId;
      let poly = zonePolysRef.current.get(z.id);
      if (!poly) {
        poly = new google.maps.Polygon({ map, clickable: true });
        poly.addListener("click", () => onSelectZoneRef.current?.(z.id));
        zonePolysRef.current.set(z.id, poly);
      }
      poly.setOptions({
        paths: z.points,
        fillColor: color,
        fillOpacity: isSel ? 0.3 : 0.16,
        strokeColor: color,
        strokeOpacity: 0.9,
        strokeWeight: isSel ? 3 : 2,
        zIndex: isSel ? 3 : 1,
      });
    });
    zonePolysRef.current.forEach((poly, id) => {
      if (!seen.has(id)) {
        poly.setMap(null);
        zonePolysRef.current.delete(id);
      }
    });
  }, [zones, selectedZoneId, isLoaded, libraries]);

  // ── The editable zone polygon (draw / drag / delete vertices) ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded || !libraries) {
      return undefined;
    }

    if (!editingZone) {
      editZoneListenersRef.current.forEach((l) => l.remove());
      editZoneListenersRef.current = [];
      editZonePolyRef.current?.setMap(null);
      editZonePolyRef.current = null;
      return undefined;
    }

    const color = editingZone.color || DEFAULT_MARKER_COLOR;

    // Create the editable polygon once per editing session; the polygon's own path
    // is the source of truth afterwards (listeners push changes back up), so we
    // don't re-seed it on every prop update (that would fight the user's edits).
    if (!editZonePolyRef.current) {
      const poly = new google.maps.Polygon({
        map,
        editable: true,
        clickable: true,
        fillColor: color,
        fillOpacity: 0.25,
        strokeColor: color,
        strokeWeight: 3,
        zIndex: 5,
      });
      // setPath (even with []) guarantees getPath() returns a real MVCArray — an
      // empty `paths` option leaves it undefined and crashes the listeners below.
      poly.setPath(editingZone.points && editingZone.points.length ? editingZone.points : []);
      editZonePolyRef.current = poly;
      const sync = () => onEditingZoneChangeRef.current?.(pathToPoints(poly));
      const path = poly.getPath();
      editZoneListenersRef.current.push(
        path.addListener("set_at", sync),
        path.addListener("insert_at", sync),
        path.addListener("remove_at", sync),
        poly.addListener("rightclick", (e: google.maps.PolyMouseEvent) => {
          if (e.vertex != null) {
            poly.getPath().removeAt(e.vertex);
            onEditingZoneChangeRef.current?.(pathToPoints(poly));
          }
        }),
      );
    } else {
      const poly = editZonePolyRef.current;
      // Fill/stroke follow the panel's live color choice.
      poly.setOptions({ fillColor: color, strokeColor: color });
      // Edit sessions seed their points one render AFTER the polygon is first
      // created (the page sets editingZonePoints via state), so the polygon is
      // born empty. When those initial points arrive, adopt them — but only while
      // it's still empty, so we never clobber the user's in-progress edits.
      const path = poly.getPath();
      if (path.getLength() === 0 && (editingZone.points?.length ?? 0) > 0) {
        // Push into the EXISTING path array — NOT poly.setPath(), which swaps in a
        // fresh MVCArray and orphans the set_at/insert_at/remove_at listeners bound
        // below, so later vertex edits would stop syncing back (and Save would send
        // the pre-edit points).
        (editingZone.points as MapPoint[]).forEach((p) => path.push(new google.maps.LatLng(p.lat, p.lng)));
      }
    }

    return undefined;
  }, [editingZone, isLoaded, libraries]);

  // ── Edge-number badges on the zone being edited (so the panel's "Edge N"
  //    list maps to a spot on the map). One label at each edge's midpoint. ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded || !libraries) {
      return;
    }

    const markerLib = libraries.marker;
    const pts = editingZone?.points ?? [];

    // Tear down and rebuild — cheap (a handful of edges) and always correct as
    // vertices are added / dragged / removed.
    editZoneEdgeLabelsRef.current.forEach((m) => (m.map = null));
    editZoneEdgeLabelsRef.current = [];

    if (pts.length < 2) {
      return;
    }

    // With 3+ points the shape is closed, so label the closing edge too.
    const edgeCount = pts.length >= 3 ? pts.length : pts.length - 1;
    for (let i = 0; i < edgeCount; i++) {
      const a = pts[i];
      const b = pts[(i + 1) % pts.length];
      const mid = { lat: (a.lat + b.lat) / 2, lng: (a.lng + b.lng) / 2 };

      const badge = document.createElement("div");
      badge.textContent = String(i + 1);
      badge.style.cssText =
        "display:flex;align-items:center;justify-content:center;min-width:18px;height:18px;padding:0 4px;" +
        "border-radius:9px;background:#1d1d1f;color:#fff;font-size:11px;font-weight:700;line-height:1;" +
        "box-shadow:0 1px 3px rgba(0,0,0,0.4);transform:translateY(-1px);";

      const marker = new markerLib.AdvancedMarkerElement({ map, position: mid, content: badge, zIndex: 6 });
      editZoneEdgeLabelsRef.current.push(marker);
    }
  }, [editingZone, isLoaded, libraries]);

  // ── Fit the map to all pins once (first time we have any) ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded || !libraries || didFitRef.current || placedElements.length === 0) {
      return;
    }

    if (placedElements.length === 1) {
      map.setCenter({ lat: placedElements[0].latitude as number, lng: placedElements[0].longitude as number });
      map.setZoom(DEFAULT_ZOOM);
    } else {
      const bounds = new google.maps.LatLngBounds();
      placedElements.forEach((el) => bounds.extend({ lat: el.latitude as number, lng: el.longitude as number }));
      map.fitBounds(bounds, 64);
    }
    didFitRef.current = true;
  }, [placedElements, isLoaded, libraries]);

  // ── Late-arriving venue center: if nothing is placed yet, recenter on it once
  //    the coordinates load (they're fetched async after the map is created). ──
  const didCenterRef = useRef(false);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || didCenterRef.current || didFitRef.current || placedElements.length > 0) {
      return;
    }
    if (center && typeof center.lat === 'number' && typeof center.lng === 'number') {
      map.setCenter(center);
      map.setZoom(DEFAULT_ZOOM);
      didCenterRef.current = true;
    }
  }, [center, placedElements.length]);

  // ── Imperative pan: jump the map to a searched place. ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focusPoint || typeof focusPoint.lat !== 'number' || typeof focusPoint.lng !== 'number') {
      return;
    }
    map.panTo(focusPoint);
    map.setZoom(17);
  }, [focusPoint]);

  // ── Cleanup ──
  useEffect(() => {
    // ⓘ Each ref holds ONE Map for the component's life (never reassigned), so the Map captured here is the one the
    // cleanup must empty (#129).
    const zonePolys = zonePolysRef.current;
    const markers = markersRef.current;

    return () => {
      clickListenerRef.current?.remove();
      overlayRef.current?.setMap(null);
      overlayRef.current = null;
      if (venueMarkerRef.current) {
        venueMarkerRef.current.map = null;
        venueMarkerRef.current = null;
      }
      editZoneListenersRef.current.forEach((l) => l.remove());
      editZoneListenersRef.current = [];
      editZonePolyRef.current?.setMap(null);
      editZonePolyRef.current = null;
      editZoneEdgeLabelsRef.current.forEach((m) => (m.map = null));
      editZoneEdgeLabelsRef.current = [];
      zonePolys.forEach((p) => p.setMap(null));
      zonePolys.clear();
      markers.forEach((m) => (m.map = null));
      markers.clear();
      mapRef.current = null;
    };
  }, []);

  const radius = "var(--mantine-radius-md)";

  if (error) {
    return (
      <Center style={{ height, border: "1px solid var(--mantine-color-gray-3)", borderRadius: radius }}>
        <Stack gap={6} align="center">
          <AppIcon iconName="MapPin" size={24} />
          <Text size="sm" c="dimmed">
            {t("events:map.mapUnavailable", { defaultValue: "Map couldn’t load. Check the connection and try again." })}
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Box style={{ position: "relative", borderRadius: radius, overflow: "hidden", border: "1px solid var(--mantine-color-gray-3)" }}>
      <div ref={containerRef} style={{ width: "100%", height }} />
      {(!isLoaded || isLoading) && (
        <Center style={{ position: "absolute", inset: 0, backgroundColor: "var(--mantine-color-body)" }}>
          <Loader size="sm" />
        </Center>
      )}
    </Box>
  );
});

EventMapBoard.displayName = "EventMapBoard";
